import * as z from 'zod';

export const GenerateAttachmentUploadUrlRequest = z.object({
  contactId: z
    .string()
    .describe('ID of the contact for whom the attachment is being uploaded.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  fileName: z
    .string()
    .describe(
      'File name of the attachment including the extension, for example, `contact-cv.pdf`.'
    )
    .min(1)
    .max(260),
  options: z.object({
    mimeType: z
      .string()
      .describe(
        'Mime type of the attachment file, for example, `application/pdf`.\nSee [supported mime types](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/attachments/attachment-v4/supported-mime-types).'
      )
      .min(1)
      .max(255),
  }),
});
export const GenerateAttachmentUploadUrlResponse = z.object({
  uploadUrl: z
    .string()
    .describe('The URL for uploading a file as an attachment to the contact.')
    .url()
    .optional(),
});
export const ListAttachmentsRequest = z.object({
  contactId: z
    .string()
    .describe('Contact ID for the attachments to be listed.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe(
              'Number of items to return.\n\nDefault: `100`\nMax: `100`'
            )
            .min(0)
            .max(100)
            .optional()
            .nullable(),
          offset: z
            .number()
            .int()
            .describe('Number of items to skip in the current sort order.')
            .min(0)
            .optional()
            .nullable(),
        })
        .describe('Allow paginating, default: limit = 100 and offset = 0')
        .optional(),
    })
    .optional(),
});
export const ListAttachmentsResponse = z.object({
  attachments: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Attachment ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          previewImage: z
            .string()
            .describe(
              'Details of the preview image reduced size when the attachment is of type `IMAGE`.'
            )
            .optional(),
          fileName: z
            .string()
            .describe('Name of the attachment file.')
            .optional(),
          mimeType: z
            .string()
            .describe(
              'Mime type of the attachment. See [supported mime types](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/attachments/attachment-v4/supported-mime-types).'
            )
            .optional(),
          attachmentType: z
            .enum(['UNKNOWN', 'IMAGE', 'OTHER'])
            .describe('Type of the attachment.')
            .optional(),
        }),
        z.xor([
          z.object({
            image: z.never().optional(),
            document: z.never().optional(),
          }),
          z.object({
            document: z.never().optional(),
            image: z
              .string()
              .describe('Image details when the attachment type is `IMAGE`.'),
          }),
          z.object({
            image: z.never().optional(),
            document: z
              .string()
              .describe(
                'Document details when the attachment type is `OTHER`.'
              ),
          }),
        ])
      )
    )
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe('Total number of items that match the query.')
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe('Whether the server failed to calculate the `total` field.')
        .optional()
        .nullable(),
    })
    .describe('Metadata for the paginated results.')
    .optional(),
});
export const DeleteAttachmentRequest = z.object({
  identifiers: z.object({
    contactId: z
      .string()
      .describe('Contact ID for the attachment to delete.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    attachmentId: z
      .string()
      .describe('Attachment ID to delete.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
  }),
});
export const DeleteAttachmentResponse = z.object({});
export const GetAttachmentRequest = z.object({
  identifiers: z.object({
    contactId: z
      .string()
      .describe('Contact ID for the attachment to retrieve.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    attachmentId: z
      .string()
      .describe('Attachment ID to retrieve.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
  }),
});
export const GetAttachmentResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Attachment ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional(),
    previewImage: z
      .string()
      .describe(
        'Details of the preview image reduced size when the attachment is of type `IMAGE`.'
      )
      .optional(),
    fileName: z.string().describe('Name of the attachment file.').optional(),
    mimeType: z
      .string()
      .describe(
        'Mime type of the attachment. See [supported mime types](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/attachments/attachment-v4/supported-mime-types).'
      )
      .optional(),
    attachmentType: z
      .enum(['UNKNOWN', 'IMAGE', 'OTHER'])
      .describe('Type of the attachment.')
      .optional(),
  }),
  z.xor([
    z.object({ image: z.never().optional(), document: z.never().optional() }),
    z.object({
      document: z.never().optional(),
      image: z
        .string()
        .describe('Image details when the attachment type is `IMAGE`.'),
    }),
    z.object({
      image: z.never().optional(),
      document: z
        .string()
        .describe('Document details when the attachment type is `OTHER`.'),
    }),
  ])
);
